package com.sddamico.draftmachine.util

import com.sddamico.draftmachine.components.sections.AppNav
import com.sddamico.draftmachine.ds.motion.Motion
import com.sddamico.draftmachine.ds.motion.ViewTransitions
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import kotlinx.browser.window
import org.w3c.dom.url.URL

/** How moving from one route to another should look: a [Motion] and, optionally, a morph element. */
internal data class RouteMotion(val motion: Motion?, val morphKey: String? = null)

private const val WIZARD = "/draft/new"
private const val LOGIN = "/login"
private val TAB_ROOTS = AppNav.entries.map { it.route }.toSet()
private val DRAFT_ROOM = Regex("^/draft/([^/]+)$")
private val POOL_DETAIL = Regex("^/pools/([^/]+)$")

fun draftMorphKey(draftId: String): String = "draft-$draftId"
fun poolMorphKey(poolId: String): String = "pool-$poolId"

/**
 * The motion for a navigation between two paths (no query string). Going deeper reads as forward,
 * going up as back; the new-draft wizard is a sheet over whatever opened it; sibling sections and
 * detours through login just fade.
 */
internal fun routeMotion(fromPath: String, toPath: String): RouteMotion {
  val from = fromPath.normalized()
  val to = toPath.normalized()
  return when {
    from == to -> RouteMotion(null)
    to == WIZARD -> RouteMotion(Motion.Present)
    from == WIZARD -> RouteMotion(Motion.Dismiss)
    from == "/" && to == LOGIN -> RouteMotion(Motion.Forward)
    from == LOGIN && to == "/" -> RouteMotion(Motion.Back)
    from == LOGIN || to == LOGIN -> RouteMotion(Motion.Fade)
    from in TAB_ROOTS && to in TAB_ROOTS -> RouteMotion(Motion.Fade)
    isAncestor(to, from) -> RouteMotion(Motion.Back, morphKey(parent = to, child = from))
    else -> RouteMotion(Motion.Forward, morphKey(parent = from, child = to))
  }
}

private fun String.normalized(): String = if (length > 1) trimEnd('/') else this

private fun isAncestor(ancestor: String, path: String): Boolean =
  ancestor == "/" || path.startsWith("$ancestor/")

/** A list row that opens a detail screen grows into it, and the screen shrinks back into the row. */
private fun morphKey(parent: String, child: String): String? {
  DRAFT_ROOM.find(child)?.let { match ->
    val id = match.groupValues[1]
    if (parent == "/" && "/draft/$id" != WIZARD) return draftMorphKey(id)
  }
  POOL_DETAIL.find(child)?.let { match ->
    val id = match.groupValues[1]
    if (parent == "/pools" && id != "new") return poolMorphKey(id)
  }
  return null
}

// The path on screen. History pops report only where they land, so this remembers where they left.
private var shownPath = window.location.pathname

/**
 * Navigate to [pathQueryAndFragment] with the screen transition its route calls for. Prefer this to
 * [Router.navigateTo] for every in-app navigation; pass [motion] only to override the inferred one.
 */
fun Router.transitionTo(
  pathQueryAndFragment: String,
  motion: Motion? = null,
  updateHistoryMode: UpdateHistoryMode = UpdateHistoryMode.PUSH,
) {
  val inferred = routeMotion(window.location.pathname, URL(pathQueryAndFragment, window.location.href).pathname)
  ViewTransitions.run(motion ?: inferred.motion, inferred.morphKey) {
    navigateTo(pathQueryAndFragment, updateHistoryMode)
    shownPath = window.location.pathname
  }
}

/**
 * Gives the browser's back and forward buttons the same transitions as in-app navigation. Must run
 * after Kobweb's router exists, since it wraps the router's own `popstate` handler.
 */
fun installHistoryTransitions() {
  shownPath = window.location.pathname
  val routerHandler = window.onpopstate
  window.onpopstate = { event ->
    val route = routeMotion(shownPath, window.location.pathname)
    shownPath = window.location.pathname
    // iOS Safari animates its own edge-swipe back gesture; a second animation on top looks broken.
    val motion = if (event.asDynamic().hasUAVisualTransition == true) null else route.motion
    ViewTransitions.run(motion, route.morphKey) { routerHandler?.invoke(event) }
  }
}
